import React, { useState, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import {
  Wand2, Copy, Check, BarChart2, AlertTriangle, CheckCircle2,
  RefreshCw, ChevronRight, Zap, Eye, Info
} from 'lucide-react';

// ─── Flesch-Kincaid Grade Level ───────────────────────────────────────────────
function countSyllables(word: string): number {
  word = word.toLowerCase().replace(/[^a-z]/g, '');
  if (!word) return 0;
  word = word.replace(/e$/, '');
  const matches = word.match(/[aeiouy]+/g);
  return Math.max(1, matches ? matches.length : 1);
}

function fleschKincaid(text: string): number {
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 2);
  const words: string[] = text.match(/\b\w+\b/g) || [];
  if (!sentences.length || !words.length) return 0;
  const syllables: number = words.reduce((sum: number, w: string) => sum + countSyllables(w), 0);
  const asl = words.length / sentences.length;      // avg sentence length
  const asw = syllables / words.length;              // avg syllables per word
  return Math.max(0, Math.round((0.39 * asl + 11.8 * asw - 15.59) * 10) / 10);
}

// ─── Sentence length variance ─────────────────────────────────────────────────
function sentenceLengthVariance(text: string): number {
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 2);
  if (sentences.length < 2) return 0;
  const lengths = sentences.map(s => (s.match(/\b\w+\b/g) || []).length);
  const mean    = lengths.reduce((a, b) => a + b, 0) / lengths.length;
  const variance = lengths.reduce((sum, l) => sum + Math.pow(l - mean, 2), 0) / lengths.length;
  return Math.round(Math.sqrt(variance) * 10) / 10; // std deviation
}

// ─── Natural transition starters ─────────────────────────────────────────────
const TRANSITIONS = [
  "Look, ", "Here's the thing — ", "Honestly, ", "In practice, ",
  "Think about it: ", "The reality is, ", "Worth noting: ",
  "What this means is, ", "Simply put, ", "At its core, ",
];

const SYNONYM_MAP: Record<string, string[]> = {
  'utilize':       ['use', 'apply', 'leverage'],
  'implement':     ['roll out', 'put in place', 'deploy'],
  'facilitate':    ['help', 'enable', 'support'],
  'leverage':      ['use', 'tap into', 'make use of'],
  'seamlessly':    ['smoothly', 'without friction', 'with ease'],
  'robust':        ['strong', 'reliable', 'solid'],
  'innovative':    ['fresh', 'new', 'novel', 'forward-thinking'],
  'furthermore':   ['also', 'on top of that', 'besides that'],
  'consequently':  ['as a result', 'so', 'because of this'],
  'nevertheless':  ['still', 'even so', 'that said'],
  'demonstrates':  ['shows', 'proves', 'highlights'],
  'significant':   ['notable', 'major', 'meaningful'],
  'however':       ['but', 'yet', 'still', 'that said'],
  'therefore':     ['so', 'as a result', 'which means'],
  'additionally':  ['also', 'plus', 'and', 'on top of that'],
  'importantly':   ['crucially', 'notably', 'above all'],
  'essentially':   ['basically', 'at its core', 'in effect'],
  'optimal':       ['best', 'ideal', 'top'],
  'streamline':    ['simplify', 'speed up', 'clean up'],
  'comprehensive': ['thorough', 'complete', 'full'],
  'delve':         ['dig', 'explore', 'look into'],
  'landscape':     ['space', 'field', 'environment'],
  'ecosystem':     ['environment', 'space', 'network'],
};

// ─── Core transformation engine ──────────────────────────────────────────────
function humanizeText(
  text: string,
  perplexityMultiplier: number,   // 1-100
  burstinessFactor: number,       // 1-100
  injectIdioms: boolean
): string {
  const pRate   = perplexityMultiplier / 100;  // 0-1 probability of synonym swap
  const bRate   = burstinessFactor / 100;       // 0-1 probability of sentence restructure

  // Step 1: Synonym substitution (perplexity)
  let processed = text.replace(/\b(\w+)\b/g, (match) => {
    const lower = match.toLowerCase();
    const syns  = SYNONYM_MAP[lower];
    if (syns && Math.random() < pRate * 0.85) {
      const replacement = syns[Math.floor(Math.random() * syns.length)];
      // Preserve capitalisation
      if (match[0] === match[0].toUpperCase()) {
        return replacement.charAt(0).toUpperCase() + replacement.slice(1);
      }
      return replacement;
    }
    return match;
  });

  // Step 2: Burstiness — sentence length variance
  const sentences = processed.split(/(?<=[.!?])\s+/);
  const transformed: string[] = [];

  for (let i = 0; i < sentences.length; i++) {
    const s    = sentences[i].trim();
    const words = s.match(/\b\w+\b/g) || [];

    if (words.length > 20 && Math.random() < bRate * 0.7) {
      // Split long sentence at midpoint connector
      const mid    = Math.floor(words.length / 2);
      const first  = words.slice(0, mid).join(' ');
      const second = words.slice(mid).join(' ');
      transformed.push(first + '.');
      if (injectIdioms && Math.random() < 0.4) {
        const t = TRANSITIONS[Math.floor(Math.random() * TRANSITIONS.length)];
        transformed.push(t + second.charAt(0).toLowerCase() + second.slice(1) + '.');
      } else {
        transformed.push(second.charAt(0).toUpperCase() + second.slice(1));
      }
    } else if (words.length < 8 && i > 0 && Math.random() < bRate * 0.3) {
      // Absorb very short sentence into previous (simulated clause)
      const prev = transformed.pop() ?? '';
      transformed.push(prev.replace(/[.!?]$/, '') + ', ' + s.charAt(0).toLowerCase() + s.slice(1));
    } else {
      // Inject transition on every 3rd sentence if idioms enabled
      if (injectIdioms && i > 0 && i % 3 === 0 && Math.random() < bRate * 0.5) {
        const t = TRANSITIONS[Math.floor(Math.random() * TRANSITIONS.length)];
        transformed.push(t + s.charAt(0).toLowerCase() + s.slice(1));
      } else {
        transformed.push(s);
      }
    }
  }

  return transformed.join(' ').replace(/\s{2,}/g, ' ').trim();
}

// ─── Score calculations ───────────────────────────────────────────────────────
function computeScores(
  original: string,
  humanized: string,
  perplexity: number,
  burstiness: number
): { zerogpt: number; winstonRisk: 'High' | 'Medium' | 'Low'; fkGrade: number; variance: number } {
  const origVar  = sentenceLengthVariance(original);
  const humanVar = sentenceLengthVariance(humanized);
  const varImprovement = Math.min(1, (humanVar - origVar) / Math.max(1, origVar));

  // Simulated ZeroGPT score: starts at ~92%, decreases with slider + variance
  const baseScore   = 92;
  const reduction   = (perplexity * 0.45 + burstiness * 0.35 + varImprovement * 20) / 100;
  const zerogpt     = Math.max(3, Math.round(baseScore - reduction * 85));

  // Winston risk bucket
  const winstonRisk = zerogpt > 50 ? 'High' : zerogpt > 22 ? 'Medium' : 'Low';

  return {
    zerogpt,
    winstonRisk,
    fkGrade: fleschKincaid(humanized),
    variance: Math.round(humanVar * 10) / 10,
  };
}

// ─── Gauge component ──────────────────────────────────────────────────────────
const GaugeBar: React.FC<{ value: number; max: number; label: string; color: string; unit?: string }> = ({
  value, max, label, color, unit = '%'
}) => (
  <div>
    <div className="flex justify-between items-center mb-1">
      <span className="text-xs text-slate-400">{label}</span>
      <span className={`text-sm font-bold ${color}`}>{value}{unit}</span>
    </div>
    <div className="h-2.5 bg-slate-700/60 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-700 ease-out ${color.replace('text-', 'bg-')}`}
        style={{ width: `${Math.min(100, (value / max) * 100)}%` }}
      />
    </div>
  </div>
);

// ─── Slider ───────────────────────────────────────────────────────────────────
const Slider: React.FC<{ label: string; desc: string; value: number; onChange: (v: number) => void; color: string }> = ({
  label, desc, value, onChange, color
}) => (
  <div className="space-y-2">
    <div className="flex justify-between items-center">
      <div>
        <p className="text-sm font-semibold text-white">{label}</p>
        <p className="text-[11px] text-slate-500">{desc}</p>
      </div>
      <span className={`text-lg font-bold ${color}`}>{value}</span>
    </div>
    <input
      type="range" min={1} max={100} value={value}
      onChange={e => onChange(Number(e.target.value))}
      className="w-full h-2 bg-slate-700 rounded-full appearance-none cursor-pointer accent-violet-500"
    />
    <div className="flex justify-between text-[10px] text-slate-600">
      <span>Conservative</span><span>Aggressive</span>
    </div>
  </div>
);

// ─── Main page ────────────────────────────────────────────────────────────────
const AIHumanizer: React.FC = () => {
  const [input,      setInput]      = useState('');
  const [output,     setOutput]     = useState('');
  const [perplexity, setPerplexity] = useState(75);
  const [burstiness, setBurstiness] = useState(80);
  const [idioms,     setIdioms]     = useState(true);
  const [running,    setRunning]    = useState(false);
  const [copied,     setCopied]     = useState(false);
  const [scores,     setScores]     = useState<ReturnType<typeof computeScores> | null>(null);
  const animRef = useRef<ReturnType<typeof setTimeout>>(null);

  const wordCount = (t: string) => (t.match(/\b\w+\b/g) || []).length;

  const handleRun = useCallback(async () => {
    if (wordCount(input) < 20) return;
    setRunning(true);
    setOutput('');
    setScores(null);

    // Simulate a slight processing delay for UX
    await new Promise(r => setTimeout(r, 600));
    const humanized = humanizeText(input, perplexity, burstiness, idioms);
    const s         = computeScores(input, humanized, perplexity, burstiness);

    setOutput(humanized);

    // Animate gauge: count down from original high score to computed score
    let anim = 92;
    const step = Math.ceil((92 - s.zerogpt) / 20);
    const tick = () => {
      anim = Math.max(s.zerogpt, anim - step);
      setScores({ ...s, zerogpt: anim });
      if (anim > s.zerogpt) {
        animRef.current = setTimeout(tick, 40);
      }
    };
    tick();
    setRunning(false);
  }, [input, perplexity, burstiness, idioms]);

  const handleCopy = async () => {
    if (!output) return;
    try { await navigator.clipboard.writeText(output); }
    catch { const el = document.createElement('textarea'); el.value = output; document.body.appendChild(el); el.select(); document.execCommand('copy'); document.body.removeChild(el); }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const winstonColor = (r: 'High' | 'Medium' | 'Low') =>
    r === 'High' ? 'text-rose-400 bg-rose-500/15 border-rose-500/30' :
    r === 'Medium' ? 'text-amber-400 bg-amber-500/15 border-amber-500/30' :
    'text-emerald-400 bg-emerald-500/15 border-emerald-500/30';

  const inputWords = wordCount(input);
  const tooShort   = inputWords < 20 && input.length > 0;
  const tooLong    = inputWords > 2000;

  return (
    <Layout>
      <div className="flex-1 overflow-y-auto p-6">

        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center shadow-[0_0_16px_rgba(139,92,246,0.4)]">
              <Wand2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">AI Content Humanizer</h1>
              <p className="text-sm text-slate-400">Bypass AI detection by restructuring perplexity &amp; burstiness signatures</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-slate-500 bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.07] rounded-full px-3 py-1.5">
            <Eye className="w-3.5 h-3.5" />
            <span>ZeroGPT · Winston AI · Copyleaks</span>
          </div>
        </div>

        {/* Split panel */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">

          {/* ── LEFT: Input + Controls ─────────────────────────────────── */}
          <div className="space-y-4">
            <div className="rounded-xl bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.07] overflow-hidden">
              <div className="px-4 py-3 border-b border-white/[0.05] flex items-center justify-between">
                <p className="text-sm font-semibold text-white">Input: AI-Generated Text</p>
                <div className={`text-[11px] px-2 py-0.5 rounded-full border ${tooShort ? 'text-amber-400 border-amber-500/30 bg-amber-500/10' : tooLong ? 'text-rose-400 border-rose-500/30 bg-rose-500/10' : 'text-slate-500 border-slate-700/40'}`}>
                  {inputWords} / 2,000 words
                </div>
              </div>
              <textarea
                className="w-full h-72 bg-transparent px-4 py-3 text-sm text-slate-200 placeholder:text-slate-600 resize-none focus:outline-none leading-relaxed"
                placeholder="Paste your AI-generated content here (minimum 20 words, maximum 2,000 words)…&#10;&#10;Example: 'Artificial intelligence has fundamentally transformed the way organizations leverage data to facilitate decision-making processes. Furthermore, the implementation of robust machine learning frameworks enables teams to utilize predictive analytics seamlessly…'"
                value={input}
                onChange={e => setInput(e.target.value)}
              />
              {tooShort && (
                <div className="px-4 pb-3 flex items-center gap-2 text-[11px] text-amber-400">
                  <AlertTriangle className="w-3.5 h-3.5" /> Minimum 20 words required
                </div>
              )}
            </div>

            {/* Sliders */}
            <div className="rounded-xl bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.07] p-4 space-y-5">
              <Slider
                label="Perplexity Multiplier"
                desc="Vocabulary variance — swaps predictable LLM word choices with natural synonyms"
                value={perplexity}
                onChange={setPerplexity}
                color="text-violet-400"
              />
              <div className="border-t border-white/[0.05]" />
              <Slider
                label="Burstiness Factor"
                desc="Sentence length variance — breaks uniform AI cadence with short/complex blends"
                value={burstiness}
                onChange={setBurstiness}
                color="text-cyan-400"
              />
            </div>

            {/* Idiom toggle */}
            <div className="rounded-xl bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.07] p-4 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-white">Inject Idiomatic Variance</p>
                <p className="text-[11px] text-slate-500 mt-0.5">Prepends natural transitions ("Look,", "Here's the thing,", "Honestly,") to enforce casual, human-sounding flow.</p>
              </div>
              <button
                onClick={() => setIdioms(v => !v)}
                className={`relative inline-flex items-center w-12 h-6 rounded-full transition-colors flex-shrink-0 ${idioms ? 'bg-violet-600' : 'bg-slate-800'}`}
              >
                <span className={`absolute w-5 h-5 rounded-full bg-white shadow transition-transform ${idioms ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>

            {/* Run button */}
            <button
              onClick={handleRun}
              disabled={running || inputWords < 20 || tooLong}
              className="w-full flex items-center justify-center gap-3 py-3.5 rounded-xl bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-sm transition-all shadow-[0_4px_20px_rgba(139,92,246,0.35)] hover:shadow-[0_4px_28px_rgba(139,92,246,0.5)]"
            >
              {running ? (
                <><RefreshCw className="w-4 h-4 animate-spin" /> Processing transformation…</>
              ) : (
                <><Zap className="w-4 h-4" /> Execute Transformation &amp; Run Bypass Audit <ChevronRight className="w-4 h-4" /></>
              )}
            </button>
          </div>

          {/* ── RIGHT: Output + Metrics ───────────────────────────────── */}
          <div className="space-y-4">
            <div className="rounded-xl bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.07] overflow-hidden">
              <div className="px-4 py-3 border-b border-white/[0.05] flex items-center justify-between">
                <p className="text-sm font-semibold text-white">Output: Humanized Text</p>
                <button
                  onClick={handleCopy}
                  disabled={!output}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1a1a2e] hover:bg-violet-600/30 disabled:opacity-40 disabled:cursor-not-allowed border border-white/[0.07] rounded-lg text-xs text-slate-300 transition-colors"
                >
                  {copied ? <><Check className="w-3.5 h-3.5 text-emerald-400" /> Copied!</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                </button>
              </div>
              <textarea
                readOnly
                className="w-full h-72 bg-transparent px-4 py-3 text-sm text-slate-200 placeholder:text-slate-600 resize-none focus:outline-none leading-relaxed"
                placeholder="Humanized content will appear here after transformation…"
                value={output}
              />
            </div>

            {/* Detection metrics */}
            <div className="rounded-xl bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.07] p-4 space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <BarChart2 className="w-4 h-4 text-violet-400" />
                <p className="text-sm font-semibold text-white">Live Bypass Audit</p>
                {!scores && <span className="text-[11px] text-slate-600 italic">— run transformation to see metrics</span>}
              </div>

              {scores ? (
                <>
                  {/* ZeroGPT score */}
                  <GaugeBar
                    value={scores.zerogpt}
                    max={100}
                    label="ZeroGPT Signature Score"
                    color={scores.zerogpt > 50 ? 'text-rose-400' : scores.zerogpt > 22 ? 'text-amber-400' : 'text-emerald-400'}
                  />

                  {/* Winston AI badge */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">Winston AI Detection Risk</span>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${winstonColor(scores.winstonRisk)}`}>
                      {scores.winstonRisk}
                    </span>
                  </div>

                  {/* FK Grade */}
                  <GaugeBar
                    value={scores.fkGrade}
                    max={20}
                    label="Flesch-Kincaid Grade Level"
                    color="text-cyan-400"
                    unit=""
                  />

                  {/* Sentence variance */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">Sentence Length Std. Dev.</span>
                    <span className="text-sm font-bold text-violet-400">{scores.variance} words</span>
                  </div>

                  {/* Summary banner */}
                  <div className={`rounded-xl p-3 border ${scores.zerogpt <= 22 ? 'bg-emerald-500/10 border-emerald-500/25' : scores.zerogpt <= 50 ? 'bg-amber-500/10 border-amber-500/25' : 'bg-rose-500/10 border-rose-500/25'}`}>
                    <div className="flex items-center gap-2">
                      {scores.zerogpt <= 22 ? <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" /> : <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />}
                      <p className="text-xs font-semibold text-white">
                        {scores.zerogpt <= 22
                          ? 'Strong bypass achieved — content reads as human-written'
                          : scores.zerogpt <= 50
                          ? 'Moderate bypass — increase sliders or re-run for better results'
                          : 'Weak bypass — raise Perplexity + Burstiness sliders and retry'}
                      </p>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1.5 ml-6">
                      ZeroGPT: {scores.zerogpt}% AI probability · Grade {scores.fkGrade} reading level · Winston: {scores.winstonRisk} risk
                    </p>
                  </div>
                </>
              ) : (
                /* Placeholder bars */
                <div className="space-y-4 opacity-30 pointer-events-none">
                  {['ZeroGPT Signature Score', 'Flesch-Kincaid Grade Level'].map(l => (
                    <GaugeBar key={l} value={0} max={100} label={l} color="text-slate-500" />
                  ))}
                </div>
              )}
            </div>

            {/* How it works info box */}
            <div className="rounded-xl bg-[#0f0f1a]/60 border border-white/[0.05] p-4">
              <div className="flex items-center gap-2 mb-2">
                <Info className="w-3.5 h-3.5 text-slate-500" />
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">How it works</p>
              </div>
              <ul className="space-y-1.5 text-[11px] text-slate-500 leading-relaxed">
                <li><span className="text-violet-400 font-medium">Perplexity:</span> Replaces statistically predictable LLM vocab with natural synonyms drawn from a curated idiom map.</li>
                <li><span className="text-cyan-400 font-medium">Burstiness:</span> Breaks uniform sentence cadence by splitting long sentences and absorbing short fragments.</li>
                <li><span className="text-pink-400 font-medium">Idioms:</span> Injects natural transition starters to mask the uniform structure common in AI-written prose.</li>
                <li className="text-slate-600 italic">Note: Detection scores are simulated indicators, not real-time API calls.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AIHumanizer;
